import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Partner dashboard: share company vacancies with Dani Job Club and follow them through to placement.
// mode="form" renders the Register Vacancy form (new, or editing `editing`); mode="list" renders Shared Vacancies.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAGE_SIZE = 10;

export const COMPANY_TYPES = [
  'Accounting / Audit / Tax Firm', 'Advertising / Media', 'Agriculture / Plantation', 'Automobile / Service Centre',
  'Bank / Finance / NBFC', 'BPO / Call Centre', 'Construction / Real Estate', 'Consultancy', 'E-commerce / Delivery',
  'Education / School / College', 'Electronics / Mobile Shop', 'Engineering / Manufacturing', 'Event Management',
  'Export / Import', 'FMCG / Distribution', 'Furniture / Interior', 'Government / PSU', 'Hospital / Clinic / Healthcare',
  'Hotel / Restaurant / Catering', 'Insurance', 'IT / Software', 'Jewellery', 'Legal Firm', 'Logistics / Transport',
  'NGO / Trust', 'Pharmacy / Medical Shop', 'Printing / Packaging', 'Resort / Tourism / Travel', 'Retail / Showroom',
  'Security Services', 'Supermarket / Hypermarket', 'Telecom', 'Textiles / Garments', 'Other'
];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = () => ({
  company_name: '',
  company_type: '',
  place: '',
  state: 'Kerala',
  district: '',
  total_vacancies: '',
  job_title: '',
  contact_person: '',
  contact_numbers: [''],
  reported_date: todayIso(),
  has_last_date: false,
  last_joining_date: '',
  remarks: ''
});

const inputCls = 'mt-1.5 w-full rounded-xl border border-yellow-200 bg-yellow-50/60 px-4 py-2.5 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-300 transition';

const statusBadge = (v) => {
  if (v.status === 'Rejected') return { label: 'Not accepted', cls: 'bg-red-100 text-red-700 border-red-200' };
  if (v.status !== 'Accepted') return { label: 'Pending Review', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  if (Number(v.placements) > 0) return { label: `🏆 ${v.placements} Placed`, cls: 'bg-emerald-600 text-white border-emerald-700' };
  if (Number(v.recorded_vacancies) > 0) return { label: 'Vacancy Live ✓', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
  return { label: 'Accepted · In Progress', cls: 'bg-yellow-400 text-black border-yellow-500' };
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ── Register / edit form ───────────────────────────────────────────────────
const VacancyForm = ({ editing, states, districtsByState, jobRoles, onSaved, onCancel }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setError('');
    setSuccess('');
    setForm(editing ? {
      company_name: editing.company_name || '',
      company_type: editing.company_type || '',
      place: editing.place || '',
      state: editing.state || 'Kerala',
      district: editing.district || '',
      total_vacancies: String(editing.total_vacancies || ''),
      job_title: editing.job_title || '',
      contact_person: editing.contact_person || '',
      contact_numbers: editing.contact_numbers?.length ? editing.contact_numbers : [''],
      reported_date: editing.reported_date || todayIso(),
      has_last_date: Boolean(editing.last_joining_date),
      last_joining_date: editing.last_joining_date || '',
      remarks: editing.remarks || ''
    } : emptyForm());
  }, [editing]);

  const set = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'state') { setForm((prev) => ({ ...prev, state: value, district: '' })); return; }
    if (name === 'total_vacancies') { set(name, value.replace(/\D/g, '').slice(0, 4)); return; }
    set(name, value);
  };
  const setNumber = (index, value) => setForm((prev) => ({
    ...prev,
    contact_numbers: prev.contact_numbers.map((n, i) => (i === index ? value.replace(/\D/g, '').slice(0, 10) : n))
  }));
  const addNumber = () => setForm((prev) => (prev.contact_numbers.length >= 5 ? prev : { ...prev, contact_numbers: [...prev.contact_numbers, ''] }));
  const removeNumber = (index) => setForm((prev) => ({ ...prev, contact_numbers: prev.contact_numbers.filter((_, i) => i !== index) }));

  const districts = districtsByState[form.state] || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const numbers = form.contact_numbers.filter(Boolean);
    if (numbers.length === 0 || numbers.some((n) => n.length !== 10)) {
      setError('Every contact number must be exactly 10 digits.');
      return;
    }
    if (form.has_last_date && !form.last_joining_date) {
      setError('Please select the last date of joining.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, contact_numbers: numbers };
      const res = editing
        ? await axios.put(`${API_URL}/api/partner/vacancies/${editing.id}`, payload, { headers: authHeaders() })
        : await axios.post(`${API_URL}/api/partner/vacancies`, payload, { headers: authHeaders() });
      if (!editing) {
        setForm(emptyForm());
        setSuccess('Vacancy shared! Our team will review it and keep you updated.');
      }
      onSaved(res.data.vacancy, Boolean(editing));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save the vacancy.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {editing ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.28em] text-blue-600">Edit Vacancy</p>
            <h2 className="mt-1 text-xl font-black text-black">Correcting: {editing.company_name}</h2>
          </div>
          <button type="button" onClick={onCancel} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-black text-black hover:border-blue-400">✕ Cancel Edit</button>
        </div>
      ) : (
        <>
          <p className="mt-4 text-xs font-black uppercase tracking-[0.28em] text-yellow-600">Register Vacancy</p>
          <h2 className="mt-1 text-2xl font-black text-black">Share a company vacancy</h2>
          <p className="mt-1 text-sm text-gray-600">Know a company that is hiring? Share it here. When we place a candidate there, you earn your commission, just like member referrals.</p>
        </>
      )}

      {error && <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}
      {success && <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">✓ {success}</div>}

      <form onSubmit={handleSubmit} className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="block md:col-span-2">
          <span className="text-sm font-bold text-black">Company Name</span>
          <input name="company_name" value={form.company_name} onChange={handleChange} className={inputCls} placeholder="e.g. Sunrise Traders" required />
        </label>

        <label className="block">
          <span className="text-sm font-bold text-black">Type of Company</span>
          <input name="company_type" list="pv-company-types" autoComplete="off" value={form.company_type} onChange={handleChange} className={inputCls} placeholder="Select or type company type" required />
          <datalist id="pv-company-types">{COMPANY_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
        </label>

        <label className="block">
          <span className="text-sm font-bold text-black">Place</span>
          <input name="place" value={form.place} onChange={handleChange} className={inputCls} placeholder="Town / locality" required />
        </label>

        <label className="block">
          <span className="text-sm font-bold text-black">State</span>
          <select name="state" value={form.state} onChange={handleChange} className={inputCls} required>
            <option value="">Select State</option>
            {states.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-bold text-black">District</span>
          {districts.length > 0 ? (
            <select name="district" value={form.district} onChange={handleChange} className={inputCls} required>
              <option value="">Select District</option>
              {districts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          ) : (
            <input name="district" value={form.district} onChange={handleChange} className={inputCls} placeholder="Type district name" required />
          )}
        </label>

        <label className="block">
          <span className="text-sm font-bold text-black">Title of Vacancy</span>
          <input name="job_title" list="pv-job-titles" autoComplete="off" value={form.job_title} onChange={handleChange} className={inputCls} placeholder="Select or type job title" required />
          <datalist id="pv-job-titles">{jobRoles.map((j) => <option key={j} value={j} />)}</datalist>
        </label>

        <label className="block">
          <span className="text-sm font-bold text-black">No. of Vacancies</span>
          <input name="total_vacancies" inputMode="numeric" value={form.total_vacancies} onChange={handleChange} className={inputCls} placeholder="e.g. 3" required />
        </label>

        <label className="block">
          <span className="text-sm font-bold text-black">Contact Person</span>
          <input name="contact_person" value={form.contact_person} onChange={handleChange} className={inputCls} placeholder="Name of HR / owner" required />
        </label>

        <div className="block">
          <span className="text-sm font-bold text-black">Contact Number{form.contact_numbers.length > 1 ? 's' : ''}</span>
          {form.contact_numbers.map((num, i) => (
            <div key={i} className="mt-1.5 flex gap-2">
              <input
                value={num}
                onChange={(e) => setNumber(i, e.target.value)}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit mobile number"
                className={`${inputCls} mt-0`}
                required={i === 0}
              />
              {form.contact_numbers.length > 1 && (
                <button type="button" onClick={() => removeNumber(i)} className="shrink-0 rounded-xl border border-red-200 px-3 text-red-600 hover:bg-red-50" aria-label="Remove number">✕</button>
              )}
            </div>
          ))}
          {form.contact_numbers.some((n) => n && n.length !== 10) && (
            <span className="mt-1 block text-xs font-bold text-red-600">Each number must be exactly 10 digits</span>
          )}
          {form.contact_numbers.length < 5 && (
            <button type="button" onClick={addNumber} className="mt-2 text-sm font-black text-yellow-700 hover:underline">+ Add another number</button>
          )}
        </div>

        <label className="block">
          <span className="text-sm font-bold text-black">Vacancy Reported Date</span>
          <input type="date" name="reported_date" value={form.reported_date} max={todayIso()} onChange={handleChange} className={inputCls} required />
        </label>

        <div className="block">
          <span className="text-sm font-bold text-black">Any last date of joining?</span>
          <div className="mt-1.5 flex gap-2">
            {[{ v: false, label: 'No' }, { v: true, label: 'Yes' }].map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, has_last_date: opt.v, last_joining_date: opt.v ? prev.last_joining_date : '' }))}
                className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-black transition ${form.has_last_date === opt.v ? 'border-yellow-500 bg-yellow-400 text-black' : 'border-yellow-200 bg-white text-gray-600 hover:bg-yellow-50'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {form.has_last_date && (
            <input type="date" name="last_joining_date" value={form.last_joining_date} min={form.reported_date} onChange={handleChange} className={inputCls} required />
          )}
        </div>

        <label className="block md:col-span-2">
          <span className="text-sm font-bold text-black">Remarks <span className="font-normal text-gray-500">(optional)</span></span>
          <textarea name="remarks" value={form.remarks} onChange={handleChange} rows={2} maxLength={2000} className={inputCls} placeholder="Salary, timings, qualification needed, anything useful…" />
        </label>

        <div className="md:col-span-2 flex justify-end gap-2">
          {editing && (
            <button type="button" onClick={onCancel} className="rounded-xl border border-gray-300 bg-white px-6 py-3 font-black text-black hover:border-yellow-400 transition">Cancel</button>
          )}
          <button type="submit" disabled={saving} className="rounded-xl bg-yellow-400 px-8 py-3 font-black text-black shadow-sm transition hover:bg-yellow-300 disabled:opacity-60">
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Share Vacancy'}
          </button>
        </div>
      </form>
    </>
  );
};

// ── Shared vacancies list ──────────────────────────────────────────────────
const VacancyList = ({ onEdit, Paginator }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/partner/vacancies`, { headers: authHeaders() })
      .then((res) => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (v) => {
    if (!window.confirm(`Delete the vacancy at ${v.company_name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/partner/vacancies/${v.id}`, { headers: authHeaders() });
      setRows((prev) => prev.filter((r) => r.id !== v.id));
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to delete vacancy.');
    }
  };

  const totals = {
    shared: rows.length,
    accepted: rows.filter((r) => r.status === 'Accepted').length,
    placed: rows.reduce((sum, r) => sum + Number(r.placements || 0), 0),
    earned: rows.reduce((sum, r) => sum + Number(r.commission_total || 0), 0)
  };
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paged = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.28em] text-yellow-600">Shared Vacancies</p>
          <h2 className="mt-1 text-xl font-black text-black">Vacancies you shared</h2>
        </div>
        <button type="button" onClick={load} className="w-fit rounded-xl border border-yellow-300 bg-white px-4 py-2 text-sm font-black text-black hover:bg-yellow-50">↺ Refresh</button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: 'Shared', value: totals.shared, cls: 'bg-white border-yellow-200 text-black' },
          { label: 'Accepted', value: totals.accepted, cls: 'bg-yellow-50 border-yellow-300 text-yellow-800' },
          { label: 'Candidates Placed', value: totals.placed, cls: 'bg-yellow-400 border-yellow-500 text-black' },
          { label: 'Commission', value: `₹${totals.earned.toFixed(2)}`, cls: 'bg-black border-black text-yellow-400' }
        ].map((c) => (
          <div key={c.label} className={`rounded-2xl border px-4 py-3 text-center ${c.cls}`}>
            <p className="text-xl font-black">{c.value}</p>
            <p className="mt-0.5 text-[10px] font-black uppercase tracking-[0.18em] opacity-70">{c.label}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="mt-4 px-2 text-sm text-gray-500">Loading vacancies…</p>
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-yellow-200 bg-yellow-50 px-4 py-8 text-center text-sm font-semibold text-yellow-700">
          No vacancies shared yet. Use <b>Register Vacancy</b> to share your first one.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="overflow-x-auto rounded-[22px] border border-yellow-200">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead>
                <tr className="bg-yellow-400">
                  {['#', 'Company', 'Vacancy', 'Location', 'Contact', 'Dates', 'Status', 'Progress', 'Action'].map((h) => (
                    <th key={h} className="border-b border-yellow-500 px-3 py-2.5 text-xs font-black uppercase tracking-wider text-black">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((v, idx) => {
                  const badge = statusBadge(v);
                  return (
                    <tr key={v.id} className="align-top odd:bg-white even:bg-yellow-50/40 hover:bg-yellow-50">
                      <td className="border-b border-yellow-100 px-3 py-3 text-xs font-bold text-gray-400">{(safePage - 1) * PAGE_SIZE + idx + 1}</td>
                      <td className="border-b border-yellow-100 px-3 py-3">
                        <p className="font-black text-black">{v.company_name}</p>
                        <p className="text-xs text-gray-500">{v.company_type}</p>
                      </td>
                      <td className="border-b border-yellow-100 px-3 py-3">
                        <p className="font-bold text-black">{v.job_title}</p>
                        <p className="text-xs text-gray-500">{v.total_vacancies} opening{Number(v.total_vacancies) === 1 ? '' : 's'}</p>
                      </td>
                      <td className="border-b border-yellow-100 px-3 py-3 text-gray-700">{v.place}, {v.district}<p className="text-xs text-gray-400">{v.state}</p></td>
                      <td className="border-b border-yellow-100 px-3 py-3 text-gray-700">
                        <p className="font-semibold">{v.contact_person}</p>
                        <p className="text-xs text-gray-500">{v.contact_numbers.join(', ')}</p>
                      </td>
                      <td className="border-b border-yellow-100 px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                        <p>Reported: {v.reported_date}</p>
                        <p>{v.last_joining_date ? `Last date: ${v.last_joining_date}` : 'No last date'}</p>
                      </td>
                      <td className="border-b border-yellow-100 px-3 py-3">
                        <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-black ${badge.cls}`}>{badge.label}</span>
                        {v.status === 'Rejected' && v.admin_note && <p className="mt-1 max-w-[180px] text-[11px] text-red-600">Reason: {v.admin_note}</p>}
                        {v.status === 'Accepted' && v.admin_note && <p className="mt-1 max-w-[180px] text-[11px] text-gray-500">Note: {v.admin_note}</p>}
                      </td>
                      <td className="border-b border-yellow-100 px-3 py-3 text-xs text-gray-600">
                        {v.status === 'Accepted' ? (
                          <>
                            <p>Follow-up: <b className="text-black">{v.ar_status || 'Pending'}</b></p>
                            <p>Placed: <b className="text-black">{v.placements || 0}</b></p>
                            {Number(v.commission_total) > 0 && <p className="font-black text-emerald-700">Earned ₹{Number(v.commission_total).toFixed(2)}</p>}
                          </>
                        ) : '—'}
                      </td>
                      <td className="border-b border-yellow-100 px-3 py-3">
                        {v.status === 'Pending' ? (
                          <div className="flex flex-col gap-1.5">
                            <button type="button" onClick={() => onEdit(v)} className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700 hover:bg-blue-100">✏️ Edit</button>
                            <button type="button" onClick={() => remove(v)} className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-black text-red-600 hover:bg-red-100">🗑 Delete</button>
                          </div>
                        ) : (
                          <span className="whitespace-nowrap text-[11px] font-bold text-gray-400">🔒 Reviewed</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Paginator page={safePage} total={rows.length} pageSize={PAGE_SIZE} onChange={setPage} />
        </div>
      )}
    </>
  );
};

const PartnerVacancies = ({ mode, editing, onEdit, onSaved, onCancelEdit, states, districtsByState, jobRoles, Paginator }) => (
  mode === 'form'
    ? <VacancyForm editing={editing} states={states} districtsByState={districtsByState} jobRoles={jobRoles} onSaved={onSaved} onCancel={onCancelEdit} />
    : <VacancyList onEdit={onEdit} Paginator={Paginator} />
);

export default PartnerVacancies;
